import { Injectable, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { ClaimsProvider } from '../ports/claims-provider.port';
import { Claim } from '../domain/types';
import { QUESTIONNAIRE_PORT } from '../../extraction-schema/ports/questionnaire.port';
import { ExtractionQuestion } from '../../extraction-schema/domain/extraction-question.model';

/**
 * ClaimsProvider that sources the annotator's claim targets from the project's
 * extraction questions. Each persisted question becomes a claim keyed by its
 * stable backend id (`q:<id>`), so evidence re-links across reloads and across
 * question-text edits. The questionnaire is the single source of truth; this
 * adapter persists nothing.
 */
@Injectable()
export class QuestionnaireClaimsAdapter implements ClaimsProvider {
  private readonly port = inject(QUESTIONNAIRE_PORT);
  private readonly route = inject(ActivatedRoute);

  async loadFor(_documentId: string): Promise<Claim[]> {
    const projectId =
      this.route.snapshot.paramMap.get('projectId') ??
      this.route.parent?.snapshot.paramMap.get('projectId');
    if (!projectId) return [];
    const questions = await firstValueFrom(this.port.getQuestions(projectId));
    return questionsToClaims(questions);
  }
}

/** Maps persisted extraction questions to ordered annotator claims. */
export function questionsToClaims(questions: ExtractionQuestion[]): Claim[] {
  return questions
    .filter((q) => q.id != null)
    .slice()
    .sort((a, b) => a.orderIndex - b.orderIndex)
    .map((q) => ({ id: `q:${q.id}`, text: q.text, order: q.orderIndex }));
}
